// @ts-check
// Imagem do perfil e logo da instituição (ImageInput do Twenty, doc 11, seções 2.1 e 3.1): escolhe o arquivo, recorta
// um quadrado (centro, com zoom) no navegador e envia um PNG pequeno; "Remover" volta para a inicial.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Avatar } from '../../ui/avatar.js';
import { Button } from '../../ui/button.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
// a imagem original pode ser grande (foto do celular); o recorte enviado tem no máximo OUTPUT_SIZE pixels de lado
const MAXIMUM_INPUT_BYTES = 15 * 1024 * 1024;
const OUTPUT_SIZE = 256;
const PREVIEW_SIZE = 240;
const MAXIMUM_ZOOM = 3;

/**
 * Quadrado do centro da imagem, com zoom, em coordenadas da imagem original.
 * @param {number} width @param {number} height @param {number} zoom
 */
export function centeredSquare(width, height, zoom) {
  const side = Math.min(width, height) / Math.max(1, zoom);
  return { x: (width - side) / 2, y: (height - side) / 2, side };
}

/**
 * @param {HTMLImageElement} image @param {number} zoom @param {string} fileName
 * @returns {Promise<File>}
 */
async function cropToFile(image, zoom, fileName) {
  const { x, y, side } = centeredSquare(image.naturalWidth, image.naturalHeight, zoom);
  const canvas = document.createElement('canvas');
  const size = Math.min(OUTPUT_SIZE, Math.round(side));
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('canvas unavailable');
  context.drawImage(image, x, y, side, side, 0, 0, size, size);
  const blob = await new Promise((resolve, reject) => canvas.toBlob((result) => (result ? resolve(result) : reject(new Error('crop failed'))), 'image/png'));
  return new File([blob], `${fileName.replace(/\.[^.]+$/, '') || 'imagem'}.png`, { type: 'image/png' });
}

/**
 * @param {{ source: { url: string, name: string }, onCancel: () => void, onConfirm: (file: File) => Promise<void>, title: string }} props
 */
function CropModal({ source, onCancel, onConfirm, title }) {
  const imageRef = useRef(/** @type {HTMLImageElement | null} */ (null));
  const [zoom, setZoom] = useState(1);
  const [natural, setNatural] = useState(/** @type {{ width: number, height: number } | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const square = natural ? centeredSquare(natural.width, natural.height, zoom) : null;
  const scale = square ? PREVIEW_SIZE / square.side : 1;

  const confirm = async () => {
    if (!imageRef.current || !natural) return;
    setIsSaving(true);
    try {
      await onConfirm(await cropToFile(imageRef.current, zoom, source.name));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };

  return html`<${Modal} title=${title} subtitle=${t('workspaceSettings.image.cropHint')} onClose=${onCancel} onConfirm=${confirm}
    confirmLabel=${t('workspaceSettings.image.save')} isConfirmDisabled=${!natural} isLoading=${isSaving}>
    <div class="image-crop">
      <div class="image-crop-frame" style=${{ width: `${PREVIEW_SIZE}px`, height: `${PREVIEW_SIZE}px` }}>
        <img ref=${imageRef} src=${source.url} alt="" draggable=${false}
          onLoad=${(event) => setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
          style=${square && natural ? {
            width: `${natural.width * scale}px`, height: `${natural.height * scale}px`, left: `${-square.x * scale}px`, top: `${-square.y * scale}px`,
          } : { visibility: 'hidden' }} />
      </div>
      <label class="image-crop-zoom">
        <span>${t('workspaceSettings.image.zoom')}</span>
        <input type="range" min="1" max=${MAXIMUM_ZOOM} step="0.05" value=${zoom}
          onInput=${(event) => setZoom(Number(/** @type {HTMLInputElement} */ (event.currentTarget).value))} />
      </label>
    </div>
  <//>`;
}

/**
 * @param {{ imageUrl: string | null, placeholder: string, seed: string, shape: 'rounded' | 'squared', disabled?: boolean,
 *   onUpload: (file: File) => Promise<void>, onRemove: () => Promise<void>, cropTitle: string }} props
 */
export function ImageUploadField({ imageUrl, placeholder, seed, shape, disabled = false, onUpload, onRemove, cropTitle }) {
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const [source, setSource] = useState(/** @type {{ url: string, name: string } | null} */ (null));
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));

  useEffect(() => () => {
    if (source) URL.revokeObjectURL(source.url);
  }, [source]);

  /** @param {Event} event */
  const chooseFile = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.currentTarget);
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError(t('workspaceSettings.image.invalidType'));
      return;
    }
    if (file.size > MAXIMUM_INPUT_BYTES) {
      setError(t('workspaceSettings.image.tooLarge'));
      return;
    }
    setError(null);
    setSource({ url: URL.createObjectURL(file), name: file.name });
  };

  return html`<div class="image-upload-field">
    <span class="image-upload-preview" data-shape=${shape}>
      ${imageUrl
        ? html`<img src=${imageUrl} alt="" />`
        : html`<${Avatar} placeholder=${placeholder || '-'} seed=${seed} size="xl" type=${shape} />`}
    </span>
    <div class="image-upload-actions">
      <div class="image-upload-buttons">
        <${Button} title=${t('workspaceSettings.image.upload')} icon="IconUpload" variant="secondary" size="small" disabled=${disabled}
          onClick=${() => inputRef.current?.click()} />
        ${imageUrl
          ? html`<${Button} title=${t('workspaceSettings.image.remove')} icon="IconTrash" variant="secondary" size="small" disabled=${disabled}
              isLoading=${isRemoving}
              onClick=${async () => {
                setIsRemoving(true);
                try {
                  await onRemove();
                } finally {
                  setIsRemoving(false);
                }
              }} />`
          : null}
      </div>
      <span class=${error ? 'image-upload-hint image-upload-error' : 'image-upload-hint'} role=${error ? 'alert' : undefined}>
        ${error ?? t('workspaceSettings.image.hint')}
      </span>
      <input ref=${inputRef} type="file" accept=${ACCEPTED_TYPES.join(',')} hidden onChange=${chooseFile} />
    </div>
    ${source
      ? html`<${CropModal} source=${source} title=${cropTitle} onCancel=${() => setSource(null)}
          onConfirm=${async (file) => {
            await onUpload(file);
            setSource(null);
          }} />`
      : null}
  </div>`;
}
